import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCrmMeta = () => {
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/crm/meta`)
      .then((r) => { if (!cancelled) setMeta(r.data); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  return meta;
};

export const useClients = (search = '') => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const r = await axios.get(`${API}/crm/clients`, { params });
      setClients(r.data.clients || []);
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { refresh(); }, [refresh]);

  return { clients, loading, refresh };
};

export const useClient = (clientId) => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crm/clients/${clientId}`);
      setClient(r.data.client);
    } catch {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { refresh(); }, [refresh]);

  const updateClient = useCallback(async (payload) => {
    const r = await axios.patch(`${API}/crm/clients/${clientId}`, payload);
    setClient(r.data.client);
    return r.data.client;
  }, [clientId]);

  const updateDocuments = useCallback(async (documents) => {
    const r = await axios.put(`${API}/crm/clients/${clientId}/documents`, { documents });
    setClient(r.data.client);
    return r.data.client;
  }, [clientId]);

  return { client, loading, refresh, updateClient, updateDocuments };
};

export const useDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/crm/dashboard`);
      setData(r.data);
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return { data, loading, refresh };
};

export const createClient = async (payload) => {
  const r = await axios.post(`${API}/crm/clients`, payload);
  return r.data.client;
};

export const deleteClient = async (clientId) => {
  await axios.delete(`${API}/crm/clients/${clientId}`);
};
